import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft,
  AlertCircle,
  Lightbulb,
  CheckCircle2,
  FileText,
  Quote,
  Sparkles,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import type { CaseStudy } from "@/content/case-studies/registry";
type RelatedCase = { slug: string; title: string; industry: string };
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: async ({
    params,
  }): Promise<{ c: CaseStudy; related: RelatedCase[] }> => {
    const [{ getCaseStudyBySlug }, { CASE_STUDY_META }] = await Promise.all([
      import("@/content/case-studies/registry"),
      import("@/content/case-studies/meta.gen"),
    ]);
    const c = getCaseStudyBySlug(params.slug);
    if (!c) throw notFound();
    const related = CASE_STUDY_META.filter(
      (x) => x.slug !== c.slug && x.industry === c.industry,
    )
      .slice(0, 3)
      .map((x) => ({ slug: x.slug, title: x.title, industry: x.industry }));
    return { c, related };
  },
  head: ({ params, loaderData }) => {
    const c = loaderData?.c;
    if (!c) {
      return pageHeadForProvince({
        title: "ไม่พบเคส",
        description: "ไม่พบเคส",
        path: `/case-studies/${params.slug}`,
      });
    }
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: c.metaTitle,
      description: c.metaDesc,
      path: `/case-studies/${c.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "เคสจริง", url: `${site}/case-studies` },
              { name: c.title, url: `${site}/case-studies/${c.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(c.faqs)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${site}/case-studies/${c.slug}#article`,
            headline: c.title,
            description: c.metaDesc,
            datePublished: c.publishedAt,
            dateModified: c.publishedAt,
            inLanguage: "th-TH",
            author: { "@id": `${site}/#organization` },
            publisher: { "@id": `${site}/#organization` },
            articleSection: c.industryLabel,
            keywords: [c.service, c.industryLabel, c.location, c.language].join(", "),
            about: {
              "@type": "Service",
              name: c.service,
              provider: { "@id": `${site}/#organization` },
            },
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "#ai-answer-tldr"],
            },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/case-studies/${c.slug}`)) },
      ],
    };
  },
  component: CaseStudyPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบเคสนี้</h1>
        <Link to="/case-studies" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับหน้ารวมเคส
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function CaseStudyPage() {
  const { c, related } = Route.useLoaderData() as {
    c: CaseStudy;
    related: RelatedCase[];
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-4xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/case-studies" className="hover:text-primary">เคสจริง</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{c.title.slice(0, 50)}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Sparkles className="h-3.5 w-3.5" />
              Case Study · {c.industryLabel}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{c.title}</h1>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>📍 {c.location}</span>
              <span>🗣️ {c.language}</span>
              <span>⏱️ {c.timeline}</span>
              <span>📅 {c.publishedAt}</span>
            </div>
            <p
              id="ai-answer-tldr"
              className="mt-5 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed"
            >
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                สรุปสั้น
              </span>
              {c.tldr}
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-4xl gap-3 px-4 py-8 md:grid-cols-4">
            {c.metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-xl border border-border bg-card p-4 text-center"
              >
                <div className="text-lg font-extrabold text-primary">{m.value}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <AlertCircle className="h-5 w-5 text-red-500" /> ความท้าทาย
            </h2>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-muted-foreground">
              {c.challenge.map((p, i) => (
                <p key={i}>• {p}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Lightbulb className="h-5 w-5 text-gold" /> วิธีแก้ของเรา
            </h2>
            <ol className="mt-4 space-y-3">
              {c.solution.map((p, i) => (
                <li
                  key={i}
                  className="flex gap-3 rounded-xl border border-border bg-card p-4"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <CheckCircle2 className="h-5 w-5 text-green-600" /> ผลลัพธ์
            </h2>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-muted-foreground">
              {c.outcome.map((p, i) => (
                <p key={i}>✓ {p}</p>
              ))}
            </div>
          </div>
        </section>

        {c.testimonial && (
          <section className="border-b border-border bg-gradient-to-b from-primary/10 to-background">
            <div className="mx-auto max-w-3xl px-4 py-10">
              <Quote className="h-8 w-8 text-gold" />
              <blockquote className="mt-2 text-lg font-medium italic leading-relaxed">
                "{c.testimonial.quote}"
              </blockquote>
              <div className="mt-3 text-sm font-bold text-muted-foreground">
                — {c.testimonial.role}
              </div>
            </div>
          </section>
        )}

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <FileText className="h-5 w-5 text-gold" /> เอกสารที่ใช้
            </h2>
            <ul className="mt-4 grid gap-2 md:grid-cols-2">
              {c.documents.map((d, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย</h2>
            <div className="mt-4 space-y-3">
              {c.faqs.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-border bg-card p-4"
                >
                  <summary className="cursor-pointer list-none font-bold">
                    <span className="mr-2 text-gold">Q.</span>
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="border-b border-border">
            <div className="mx-auto max-w-4xl px-4 py-10">
              <h2 className="text-xl font-bold">เคสอื่นในกลุ่ม {c.industryLabel}</h2>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/case-studies/$slug"
                    params={{ slug: r.slug }}
                    className="rounded-xl border border-border bg-card p-4 text-sm font-semibold transition hover:border-gold"
                  >
                    {r.title} →
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link to="/case-studies" className="text-sm font-bold text-primary hover:underline">
                  ← ดูเคสทั้งหมด
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-3xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">มีงานคล้ายเคสนี้? ปรึกษาฟรี</h2>
            <p className="mt-2 text-sm opacity-90">
              เราประเมิน timeline + เอกสารที่ต้องการให้ได้ภายในวันเดียว
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground"
              >
                โทร {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener"
                className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold"
              >
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="notary" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}